import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { SECTION_PATH, NavSection, isWorkspaceSlug, type NavDestination } from '../lib/pages';
import { useHomepage } from './HomepageContext';
import { useDashboardDuplexer } from '../lib/use-dashboard-duplexer';
import { useRevalidateOnReveal } from '../lib/use-revalidate-on-reveal';
import { useBasePath } from '../lib/use-base-path';
import { useNarrow } from '../lib/use-narrow';
import { chatHandoffLink } from '../lib/chat-handoff';
import { callWidgetTool } from '../lib/widgets';
import { DashboardErrorBanner } from './DashboardErrorBanner';
import { CONTENT_HEIGHT_SAFETY_PX } from './symphony/DashboardView/constants';
import { useTranslation } from '../lib/i18n';
import { useTheme } from '../lib/theme-context';

// Renders ONE Orion-authored page by pointing a sandboxed iframe `src` at the dashboard service's browser
// renderer. The service (wix.symphony.v1.DashboardRenderHandler, same origin — manage.wix.com) resolves the
// dashboard bento (or a drill-in page) for the CALLER's own metasite, injects every widget slot, and serves
// the FULLY-assembled document (design tokens + runtime + the `sym` bridge shim, all inline). So the host no
// longer fetches JSON and assembles a `srcDoc` on the main thread — the browser fetches, streams, and
// HTTP-caches the document natively. The host owns only the bridge + an optional header/back affordance.
//
// A page is addressed by `slug`, optionally scoped to a `workspace`. The host services the dashboard's
// `sym.*` calls; `sym.navigate` ROUTES — a drill-in becomes a real URL (workspace-scoped when we're inside
// one), so the browser back button works and each page is linkable.

// The dashboard service's browser mount (same origin as orion-web — manage.wix.com), where the RawHttp
// renderer serves the assembled document. Same-origin so the caller's Wix session rides along and the
// renderer resolves the caller's own metasite (recipient.GetMyRecipient) — there is no metaSiteId in the URL.
const RENDER_BASE = '/_api/dashboard/v1/dashboards/render';

// The served document is byte-stable and nonce-free: its bridge shim tags every message with this FIXED
// marker (the renderer pins the old per-load `${nonce}` to the constant 'sym'). The host validates inbound
// messages by BOTH `e.source === iframe.contentWindow` AND this marker, and stamps the same on its replies.
const SYM_MARKER = 'sym';

// `header` is optional host chrome rendered ABOVE the page inside the content pane (the Symphony home
// fallback passes a greeting + composer there). `onBack` renders a subtle in-app back affordance (drill-in
// pages pass it to return to the workspace dashboard); the browser back button works regardless.
export function PageSurface({
  slug,
  workspace,
  header,
  onBack,
}: {
  slug: string;
  workspace?: string;
  header?: ReactNode;
  onBack?: () => void;
}) {
  const narrow = useNarrow();
  // The served document reports its content height and the host sizes the iframe to it, so THIS pane is the
  // page's one scroll container (see `docHeight` below). Until the first report arrives the iframe fills a
  // fixed pane, exactly as it always did.
  const [docHeight, setDocHeight] = useState<number | null>(null);
  return (
    <section
      className="orion-content-pane"
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        insetInlineEnd: 0,
        zIndex: 4,
        overflowX: 'hidden',
        overflowY: docHeight != null ? 'auto' : 'hidden',
        paddingBlock: '92px 48px',
        // Horizontal gutter + page width come from the shared `.orion-content-pane`
        // rule (Symphony: 80px gutters, capped at 1280). Kept off the inline style so it wins.
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Desktop back affordance; on mobile the header back button covers it. */}
      {onBack && !narrow && <BackButton onClick={onBack} />}
      {header}
      <div style={docHeight != null ? { position: 'relative', width: '100%', flexShrink: 0 } : { flex: 1, minHeight: 0, position: 'relative' }}>
        <Dashboard slug={slug} workspace={workspace} docHeight={docHeight} onHeight={setDocHeight} />
      </div>
    </section>
  );
}

// A quiet, low-contrast back affordance — the app-level "return to the dashboard" the drill-in pages carry.
// It sits above the page content (not over the iframe) so it never overlaps the dashboard's own chrome.
function BackButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        alignSelf: 'flex-start',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        margin: '0 0 14px',
        paddingBlock: '6px',
        paddingInline: '8px 12px',
        border: 'none',
        borderRadius: 999,
        background: 'transparent',
        color: 'rgba(var(--ink-rgb),0.55)',
        fontFamily: 'inherit',
        fontSize: 13,
        fontWeight: 'var(--sym-weight-medium)',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, color 0.15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(var(--ink-rgb),0.05)';
        e.currentTarget.style.color = 'rgba(var(--ink-rgb),0.85)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = 'rgba(var(--ink-rgb),0.55)';
      }}
    >
      <svg className="dir-flip" width="16" height="16" viewBox="0 0 16 16" aria-hidden>
        <path d="M9.5 3.5L5 8l4.5 4.5" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {t('common.back')}
    </button>
  );
}

function Dashboard({
  slug,
  workspace,
  docHeight,
  onHeight,
}: {
  slug: string;
  workspace?: string;
  docHeight: number | null;
  onHeight: (height: number) => void;
}) {
  // Every error this surface hit — a failed widget tool call or an uncaught runtime error — deduped by the
  // rendered line and kept in arrival order, so a single stripe reports them all together.
  const [errors, setErrors] = useState<string[]>([]);
  const addError = useCallback((line: string) => setErrors((prev) => (prev.includes(line) ? prev : [...prev, line])), []);
  const [reloadKey, setReloadKey] = useState(0);
  const router = useRouter();
  const basePath = useBasePath();
  const { state: homepage } = useHomepage();
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // The server assembles + serves the whole document; we just point the iframe at it. Addressing mirrors the
  // legacy /api/pages resolver: the dashboard is `workspace || slug`; a slug inside a workspace is a drill-in
  // page (`?page=`). `_r` is a cache-buster bumped on a live edit so the iframe refetches — an unchanged doc
  // still revalidates to a 304 via its ETag.
  const renderUrl = useMemo(() => {
    if (!slug) {
      return '';
    }
    const dashboardSlug = workspace || slug;
    const pageSlug = workspace && slug !== workspace ? slug : undefined;
    const params = new URLSearchParams();
    if (pageSlug) {
      params.set('page', pageSlug);
    }
    if (reloadKey > 0) {
      params.set('_r', String(reloadKey));
    }
    const qs = params.toString();
    return `${RENDER_BASE}/${encodeURIComponent(dashboardSlug)}${qs ? `?${qs}` : ''}`;
  }, [slug, workspace, reloadKey]);

  // Reset any prior errors when the addressed page changes.
  useEffect(() => setErrors([]), [slug, workspace]);

  // Live update: when Orion rewrites a widget/page (a DashboardService write) the dashboard-changed signal
  // fires — bump the cache-buster so the iframe refetches the now-newer document. An unchanged document
  // revalidates to a 304 via its ETag.
  useDashboardDuplexer(() => setReloadKey((k) => k + 1));
  // A component-library publish changes what this document renders WITHOUT writing to this site's dashboard
  // rows, so it emits no per-site signal — and this surface stays mounted, so nothing would re-ask until a
  // full page load. Re-validating when the tab is revealed costs a 304 when nothing changed.
  useRevalidateOnReveal(() => setReloadKey((k) => k + 1));

  // Keep router/basePath/workspace fresh for the (stable) message listener without re-binding it.
  const navRef = useRef({ router, basePath, workspace, homepage, t });
  navRef.current = { router, basePath, workspace, homepage, t };
  const heightRef = useRef(onHeight);
  heightRef.current = onHeight;

  // Bridge relay: validate the message is from OUR iframe + carries the fixed `sym` marker, then service it.
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      const iframe = iframeRef.current;
      if (!iframe || e.source !== iframe.contentWindow) {
        return;
      }
      const d = e.data as {
        __symWidget?: string;
        id?: string;
        kind?: string;
        tool?: string;
        params?: unknown;
        prompt?: string;
        context?: string;
        title?: string;
        message?: string;
        height?: number;
        destination?: NavDestination;
      };
      if (!d || d.__symWidget !== SYM_MARKER) {
        return;
      }
      const reply = (payload: object) => iframe.contentWindow?.postMessage({ __symWidget: SYM_MARKER, ...payload }, '*');
      const { router: r, basePath: bp, workspace: ws, homepage: hp, t: translate } = navRef.current;

      if (d.kind === 'height' && typeof d.height === 'number') {
        // The document reports its own content height (bridge-shim, web only). Honouring it is not an
        // optimisation: on web the runtime clips its own overflow precisely BECAUSE the host promises to
        // size the frame to this number, so a host that ignores it strands everything below the fold.
        heightRef.current(d.height);
      } else if (d.kind === 'call' && d.id) {
        callWidgetTool(d.tool || '', d.params)
          .then((result) => reply({ id: d.id, ok: true, result }))
          .catch((err: unknown) => {
            const message = err instanceof Error ? err.message : String(err);
            reply({ id: d.id, ok: false, error: message });
            // A failed tool call is NOT itself a page error: a widget probing data that doesn't exist for
            // this account fails routinely and then renders its own empty/error state. The banner is raised
            // only when a widget actually fails to render — the runtime reports that via a `kind: 'error'`
            // message tagged with the widget (see below). Reply ok:false so the widget can react; don't banner.
          });
      } else if (d.kind === 'confirm' && d.id) {
        const ok = typeof window !== 'undefined' ? window.confirm(d.message || d.title || translate('pageSurface.confirmDefault')) : false;
        reply({ id: d.id, ok: true, result: ok });
      } else if (d.kind === 'chat') {
        const text = [d.prompt, d.context].filter(Boolean).join('\n\n');
        const link = chatHandoffLink({ kind: 'new', message: text }, bp);
        void r.push(link.route, link.path);
      } else if (d.kind === 'navigate' && d.destination) {
        const destination = d.destination;
        if ('workspace' in destination) {
          // Open that workspace's dashboard (Growth & Goals) on its own route.
          void r.push(
            { pathname: '/workspace/[slug]', query: { slug: destination.workspace } },
            `${bp}/workspace/${encodeURIComponent(destination.workspace)}`,
          );
        } else if ('page' in destination) {
          // A widget's drill-in becomes a real URL so the browser back button works. Inside a workspace it
          // stays scoped to that workspace (…/pages/<page>); on a global page it's the global /pages route.
          if (ws) {
            void r.push(
              { pathname: '/workspace/[slug]/pages/[page]', query: { slug: ws, page: destination.page } },
              `${bp}/workspace/${encodeURIComponent(ws)}/pages/${encodeURIComponent(destination.page)}`,
            );
          } else if (hp.status === 'present' && isWorkspaceSlug(hp.index, destination.page)) {
            // Recovery: a workspace opened as a `{ page }` (the authoring slip that dead-ends — a workspace is
            // its own dashboard, not a drill-in page of the home). Route to its dashboard instead of a dead-end
            // `/pages/<slug>` that resolves empty. Web-side net only: `{ workspace }` is an orion-web-local
            // destination — native + common-lib's `NavDestination` don't have it yet — so the upstream fix is
            // making `{ workspace }` a first-class cross-surface nav, not just re-authoring widgets.
            // eslint-disable-next-line no-console
            console.info('[dashboard-nav] recovered a workspace opened as a page', { slug: destination.page });
            void r.push(
              { pathname: '/workspace/[slug]', query: { slug: destination.page } },
              `${bp}/workspace/${encodeURIComponent(destination.page)}`,
            );
          } else {
            void r.push(
              { pathname: '/pages/[slug]', query: { slug: destination.page } },
              `${bp}/pages/${encodeURIComponent(destination.page)}`,
            );
          }
        } else {
          // A destination shape this host does not know must NOT move the user. `SECTION_PATH[undefined]`
          // fell through to Home, so a library widget emitting `{app: …}` (appointment-detail's "Go to
          // Booking Calendar" does) threw the reader out of the page they were reading, silently. Losing
          // your place is worse than a tap that does nothing, and a dead tap is at least diagnosable.
          const section = (destination as { section?: string }).section;
          if (section && section in SECTION_PATH) {
            void r.push(`${bp}${SECTION_PATH[section as NavSection]}`); // a built-in section is another screen
          } else {
            console.warn('[dashboard-nav] ignored a destination this host does not handle', destination);
          }
        }
      } else if (d.kind === 'error') {
        // Raised only by the runtime binder when a widget genuinely fails to render (its data function
        // threw with no cached paint) — tagged with the widget slug. The runtime no longer forwards raw
        // window errors, so this is a real, actionable per-widget failure, not incidental script noise.
        // Logged as well as collected. Since the banner classifies, a page whose only failures are a
        // missing connection or a rate limit shows a calm stripe with no "Copy details" — correct for the
        // user, but it would leave the raw diagnostic with nowhere to be read. Both phones already log
        // this line ("dashboard: widget runtime error"); web was the odd one out.
        console.warn('dashboard: widget runtime error', d.message);
        addError(d.message || translate('pageSurface.pageError'));
      } else if (d.kind === 'errorCleared') {
        // Every widget that was in the error state has recovered (an SWR revalidate, a retry, a later
        // render), so the banner has nothing left to describe. Without this the banner was write-only —
        // raised by `error` and cleared by nothing but a route change — so one transient widget failure
        // left "didn't load correctly" on screen accusing a dashboard that was already fine.
        setErrors([]);
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // The dashboard is a same-origin iframe with its OWN document, so the host's `data-theme` cannot
  // reach it through the cascade — the served document carries both palettes and is told which to
  // wear over the bridge. That is exactly what the phones do: `PageWebView.deliverTheme` posts
  // `{kind:'theme'}` on load and on every flip, and its own comment calls itself "the WebView
  // analogue of orion-web posting the theme message to its iframe".
  //
  // Web posted a hardcoded `'light'` ("Desktop has no light/dark switching for now"), which is why a
  // dark home showed dark chrome around a white dashboard. It now posts the real theme.
  //
  // A theme sent before the document is ready is DROPPED (the same rule native writes down), so this
  // fires on `onLoad` as well as on every subsequent change.
  const { theme } = useTheme();
  const postTheme = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage({ __symWidget: SYM_MARKER, kind: 'theme', theme }, '*');
  }, [theme]);

  // Re-post on a flip. No reload: the runtime recolours in place, as it does on the phones.
  useEffect(() => {
    postTheme();
  }, [postTheme]);

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: docHeight != null ? 'auto' : '100%',
        minHeight: 0,
      }}
    >
      {/* Flow, above the iframe — pushes the dashboard down instead of overlapping it. */}
      {errors.length > 0 && (
        <DashboardErrorBanner
          basePath={basePath}
          title={slug}
          errors={errors}
          palette={{ color: 'rgba(var(--ds-orange-300-rgb), 0.9)', background: 'rgba(var(--ds-text-pending-rgb), 0.08)', border: 'rgba(var(--ds-text-pending-rgb), 0.25)' }}
          onRetry={() => {
            setErrors([]);
            setReloadKey((k) => k + 1);
          }}
        />
      )}
      <div style={{ position: 'relative', width: '100%', minHeight: 0, flex: docHeight != null ? '0 0 auto' : '1 1 auto' }}>
        {renderUrl ? (
          <iframe
            ref={iframeRef}
            title={slug}
            sandbox="allow-scripts"
            src={renderUrl}
            onLoad={() => {
              postTheme();
            }}
            style={{
              display: 'block',
              width: '100%',
              height: docHeight != null ? `${docHeight + CONTENT_HEIGHT_SAFETY_PX}px` : '100%',
              border: 'none',
              background: 'transparent',
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
